<template>
  <div ref="rootRef" class="bui-tbl-page-limit">
    <button
      type="button"
      class="trigger"
      :class="{ open: isOpen }"
      :aria-expanded="isOpen"
      :aria-haspopup="true"
      @click="toggle"
    >
      <span class="value">{{ currentLabel }}</span>
      <PhCaretDown v-if="!isOpen" :size="10" weight="bold" />
      <PhCaretUp v-else :size="10" weight="bold" />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen && menuRect"
        class="bui-tbl-page-limit-menu"
        :style="menuPosStyle"
        role="listbox"
      >
        <button
          v-for="opt in normalizedOptions"
          :key="String(opt.value)"
          type="button"
          class="bui-tbl-page-limit-menu__item"
          :class="{ active: opt.value === modelValue }"
          role="option"
          :aria-selected="opt.value === modelValue"
          @click="onSelect(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { PhCaretDown, PhCaretUp } from '@phosphor-icons/vue';

type Option = { label: string; value: number | string };

const props = withDefaults(
  defineProps<{
    modelValue: number | string;
    options?: Array<number | string | Option>;
    suffix?: string;
  }>(),
  {
    options: () => [10, 25, 100],
    suffix: 'per page',
  },
);

const emit = defineEmits<{ 'update:modelValue': [value: number | string] }>();

const rootRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const menuRect = ref<DOMRect | null>(null);

const normalizedOptions = computed<Option[]>(() =>
  props.options.map((o) =>
    typeof o === 'object' ? o : { label: String(o), value: o },
  ),
);

const currentLabel = computed(() => {
  const found = normalizedOptions.value.find((o) => o.value === props.modelValue);
  const labelPart = found ? found.label : String(props.modelValue);
  return props.suffix ? `${labelPart} ${props.suffix}` : labelPart;
});

const menuPosStyle = computed(() => {
  if (!menuRect.value) return {};
  return {
    position: 'fixed' as const,
    top: `${menuRect.value.bottom + 8}px`,
    left: `${menuRect.value.left}px`,
    minWidth: `${menuRect.value.width}px`,
  };
});

function toggle() {
  if (isOpen.value) {
    isOpen.value = false;
  } else {
    measure();
    isOpen.value = true;
  }
}

function measure() {
  if (rootRef.value) {
    menuRect.value = (rootRef.value.querySelector('.trigger') as HTMLElement)?.getBoundingClientRect() || null;
  }
}

function onSelect(value: number | string) {
  emit('update:modelValue', value);
  isOpen.value = false;
}

function onDocClick(e: MouseEvent) {
  if (!isOpen.value) return;
  const t = e.target as Node | null;
  if (!t) return;
  if (rootRef.value?.contains(t)) return;
  // Menu is teleported; check globally for the open menu DOM
  const menuEls = document.querySelectorAll('.bui-tbl-page-limit-menu');
  for (const m of menuEls) {
    if (m.contains(t)) return;
  }
  isOpen.value = false;
}

function onWindowChange() {
  if (isOpen.value) measure();
}

onMounted(() => {
  document.addEventListener('click', onDocClick, true);
  window.addEventListener('resize', onWindowChange);
  window.addEventListener('scroll', onWindowChange, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true);
  window.removeEventListener('resize', onWindowChange);
  window.removeEventListener('scroll', onWindowChange, true);
});

watch(isOpen, (v) => {
  if (v) measure();
});
</script>

<style lang="scss" scoped>
.bui-tbl-page-limit {
  display: inline-flex;
  align-items: center;
}

.trigger {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  color: var(--bui-droplet-text, #ffb2db);
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  line-height: 16px;

  &:focus-visible {
    outline: 2px solid var(--bui-droplet-text, #ffb2db);
    outline-offset: 2px;
    border-radius: 2px;
  }
}

.value {
  white-space: nowrap;
}

@media (max-width: 768px) {
  .trigger {
    font-size: 11px;
    line-height: 14px;
  }
}
</style>

<style lang="scss">
// Teleported menu — needs unscoped CSS because Teleport relocates the DOM
// out of the component subtree. The dedicated class ensures we don't
// affect any other listbox.
.bui-tbl-page-limit-menu {
  background: var(--bui-droplet-menu-fill, #fffcfd);
  color: var(--bui-droplet-menu-text, #2a2969);
  border-radius: var(--bui-droplet-menu-radius, 8px);
  box-shadow: 0 8px 32px rgba(75, 24, 134, 0.32);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 9999;

  &__item {
    display: flex;
    align-items: center;
    height: 32px;
    padding: 0 12px;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: var(--bui-droplet-menu-text, #2a2969);
    font-family: inherit;
    font-size: 14px;
    line-height: 17px;
    cursor: pointer;
    text-align: left;

    &:hover {
      background: rgba(42, 41, 105, 0.06);
    }

    &.active {
      background: rgba(164, 32, 200, 0.12);
      color: var(--bui-tbl-head-fill, #a420c8);
      font-weight: 500;
    }
  }
}
</style>
